'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FeedList, useCommunityInteraction, usePostReferenceFeed } from '@/app/components/shared/Community';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { useExtraTag, useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { CommunityEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useCommunity } from '../_hooks/use-community';
import { apiGetMyLikes, materialsShareElementsFeed } from '../apis';
import { LIKES_SUBTYPE, PROFILE_SEARCH_OPTIONS, PROFILE_TAB_ENUM } from '../constant';
import type { CommunitySearchOption, MaterialsShareElementsFeedData } from '../types';

const FROM = 'my_likes_page';

interface LikesProps {
  userId?: string;
}

export const Likes = ({ userId }: LikesProps) => {
  const searchOptions = PROFILE_SEARCH_OPTIONS();
  const [hasMore, setHasMore] = useState(false);
  const pageToken = useRef('');
  const t = useTranslations();
  const syncFeedDetailConfig = useStore((state) => state.syncFeedDetailConfig);

  const updatePageInfo = ({ token }: { token?: string }) => {
    pageToken.current = token || '';
    setHasMore(!!token);
  };

  const { likeTag } = useExtraTag();

  const { state, setState, loading, handleSelectedCreation, vote, unVote, incrementCompleteTimes, handleLeave } =
    useCommunity({
      fetchData: !userId ? apiGetMyLikes : apiGetMyLikes,
      searchOptions,
      infinityList: true,
    });

  const { runAsync: fetchPostReference, loading: shareReferenceLoading } = useFetch(
    (params: { query: MaterialsShareElementsFeedData['query'] }) => {
      return materialsShareElementsFeed({
        query: params.query,
      });
    },
    {
      manual: true,
    },
  );

  const {
    referenceList,
    getShareReferenceData,
    handleDetail: handleShareReferenceDetail,
    updateShareReferenceInteractionStatus,
    init: initShareReferenceData,
    onExpose,
    setActiveCategory,
  } = usePostReferenceFeed({
    handleEnter: () => {},
    handleLeave: () => {},
    updatePageInfo,
    pageToken,
    fromPage: FROM,
    fetchData: fetchPostReference,
  });

  const { vote: updateReferenceVote, unVote: updateReferenceUnVote } = useCommunityInteraction({
    onInteractionStatusChange: updateShareReferenceInteractionStatus,
  });

  useEffect(() => {
    if (state.type === CommunityEnum.PostReferences) {
      pageToken.current = '';
      setHasMore(false);
      initShareReferenceData();
      getShareReferenceData();
    }
  }, [state.type]);

  useEffect(() => {
    setActiveCategory(likeTag.id);
  }, [likeTag.id]);

  const config = useMemo(() => {
    if (state.type === CommunityEnum.PostReferences) {
      return {
        getData: () => {
          getShareReferenceData();
        },
        handleDetail: (id: string) => {
          handleShareReferenceDetail(id, FROM);
        },
        vote,
        unVote,
        hasMore,
        onExpose,
        isLoading: shareReferenceLoading,
      };
    }
    return {
      getData: () => {
        setState({
          page: state.page + 1,
        });
      },
      handleDetail: (id: string) => {
        handleSelectedCreation(id);
      },
      vote,
      unVote,
      hasMore: state.total > state.items.length,
      isLoading: loading,
    };
  }, [
    state.type,
    state.items,
    state.total,
    state.page,
    loading,
    hasMore,
    shareReferenceLoading,
    vote,
    unVote,
    handleSelectedCreation,
    updateReferenceVote,
    updateReferenceUnVote,
    onExpose,
    getShareReferenceData,
    handleShareReferenceDetail,
  ]);

  const commonCommunityProps = useMemo(() => {
    return {
      handleVote: config.vote,
      handleUnVote: config.unVote,
    };
  }, [config.vote, config.unVote]);

  useEffect(() => {
    syncFeedDetailConfig({
      ...commonCommunityProps,
      onClose: () => {
        handleLeave();
        setState({ selectedAsset: undefined });
      },
      onVideoPlayEnd: incrementCompleteTimes,
      thumbScrollListConfig: {
        list: state.items,
        hasMore: config.hasMore,
        onLoadMore: config.getData,
      },
    });
  }, [state.items, config.hasMore]);

  return (
    <div className="flex flex-1 flex-col">
      <div className="sticky top-[52px] z-20 bg-system-bg08 pb-6 text-xl sm:top-10">
        <div className="hide-scrollbar flex items-center overflow-x-scroll">
          <Tabs
            value={state.type}
            onValueChange={(value) => {
              if (value !== state.type) {
                reportEvent('me_page_element_click', {
                  element_name: PROFILE_TAB_ENUM.FAVORITE,
                  subtype: LIKES_SUBTYPE[value as CommunitySearchOption],
                });
                setState({
                  type: value as CommunitySearchOption,
                  page: 1,
                  items: [],
                });
              }
            }}
          >
            <TabsList className="flex h-fit w-full gap-2 bg-transparent p-0">
              {searchOptions.map((option) => (
                <TabsTrigger
                  key={option.key}
                  className="h-7 min-w-[62px] rounded-8 bg-system-white/10 px-3 py-2 font-normal text-sm text-white capitalize data-[state=active]:bg-system-white data-[state=active]:text-system-black"
                  value={option.key}
                >
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <FeedList
        inspAssets={state.items}
        handleDetail={config.handleDetail}
        handleUnVote={config.unVote}
        handleVote={config.vote}
        referenceList={referenceList}
        active={state.type === 'all' ? CommunityEnum.MediaAsset : (state.type as CommunityEnum)}
        hasMore={config.hasMore}
        onExpose={config.onExpose}
        isLoading={config.isLoading}
        hideEmptyVoteCount={true}
        onLoadMore={async () => {
          config.getData();
        }}
        emptyListText={t('profile.emptyLike')}
      />
    </div>
  );
};
